import { Injectable } from '@angular/core';
import { EntityBase } from '~core/model';
import { Observable } from 'rxjs/Observable';
import { findIndex, cloneDeep, remove, isEmpty, merge } from 'lodash';
import { CacheService } from './cache.service';
import { environment } from '~environment';
import { HttpClient } from '@angular/common/http';
import { RateLimitService } from './rate-limit.service';
import { RequestQueueService } from './request-queue.service';

@Injectable()
export class ApiBase {

  private activeRequests = [];
  private errorMsg: string = 'Server error, please try reloading your browser!';

  constructor(
    protected _http: HttpClient, 
    private _cacheFactoryApi: CacheService, 
    private rateLimitService: RateLimitService,
    private requestQueueService: RequestQueueService
  ) { }

  protected get(url, requestOptions = {}, Model?: any, isCacheEnabled = true, cacheJsonInput = null, baseUrlEnabled = true): Observable<any> {

    if (isCacheEnabled) {
      // Get from Cache if exists
      let cacheJson = this.getCacheJSON(url, cacheJsonInput);
      if (!!cacheJson) {
        return Observable.create(observer => {
          setTimeout(() => {
            observer.next(cacheJson);
            observer.complete();
          }, 10);
        });
      }
    }

    let idx = findIndex(this.activeRequests, request => request.key === url);
    if (idx > -1) {
      return this.activeRequests[idx].value;
    }

    let apiResponse = this.requestQueueService.enqueue(
      this._http.get<any>(baseUrlEnabled ? environment.baseUrl + url : url, requestOptions || {})
        .retryWhen(errors => 
          errors.mergeMap(error => this.rateLimitService.handleRateLimitError(error))
        )
        .map(response => {
          const data = response;
          if (isCacheEnabled) {
            return this.saveDataToCache(url, data, cacheJsonInput, Model);
          } else {
            remove(this.activeRequests, request => request.key === url);
            if (!Model) {
              return data;
            }
            return data instanceof Array ? Model.fromJSONArray(data) : Model.fromJSON(data);
          }
        })
        .catch(error => {
          remove(this.activeRequests, request => request.key === url);
          return Observable.throw(error);
        })
    );

    apiResponse = apiResponse.share().map(data => cloneDeep(data));
    this.activeRequests.push({ key: url, value: apiResponse });
    return apiResponse;
  }

  protected post(url, body = null, requestOptions = {}, Model?: { fromJSON(json: any): EntityBase, fromJSONArray(json: any[]): EntityBase[] }, isCacheEnabled = true, cacheJsonInput = null, baseUrlEnabled = true): Observable<any> {

    // Get from Cache if exists
    let hash = null;
    if (!!body && !isEmpty(body)) {
      hash = JSON.stringify(body);
    } else {
      hash = url;
    }

    if (isCacheEnabled) {
      // Get from Cache if exists
      const cacheJson = this.getCacheJSON(hash, cacheJsonInput);
      if (!!cacheJson) {
        return Observable.create(observer => {
          setTimeout(() => {
            observer.next(cacheJson);
            observer.complete();
          }, 10);
        });
      }
    }

    const idx = findIndex(this.activeRequests, request => request.key === hash);
    if (idx > -1) {
      return this.activeRequests[idx].value;
    }

    let apiResponse = this._http.post(baseUrlEnabled ? environment.baseUrl + url : url, body, requestOptions || {})
      .map(response => {
        const data = !!response ? response : {};
        if (isCacheEnabled) {
          return this.saveDataToCache(hash, data, cacheJsonInput, Model);
        } else {
          remove(this.activeRequests, request => request.key === hash);
          if (!Model) {
            return data;
          }
          return data instanceof Array ? Model.fromJSONArray(data) : Model.fromJSON(data);
        }
      })
      .catch(response => {
        remove(this.activeRequests, request => request.key === hash);
        return Observable.throw(response);
      });

    apiResponse = apiResponse.share().map(data => cloneDeep(data));
    this.activeRequests.push({ key: hash, value: apiResponse });
    return apiResponse;
  }

  protected put(url, body = null, requestOptions = {}, Model?: { fromJSON(json: any): EntityBase, fromJSONArray(json: any[]): EntityBase[] }, isCacheEnabled = true, cacheJsonInput = null, baseUrlEnabled = true): Observable<any> {

    // Get from Cache if exists
    let hash = null;
    if (!!body && !isEmpty(body)) {
      hash = JSON.stringify(body);
    } else {
      hash = url;
    }

    if (isCacheEnabled) {
      // Get from Cache if exists
      const cacheJson = this.getCacheJSON(hash, cacheJsonInput);
      if (!!cacheJson) {
        return Observable.create(observer => {
          setTimeout(() => {
            observer.next(cacheJson);
            observer.complete();
          }, 10);
        });
      }
    }

    const idx = findIndex(this.activeRequests, request => request.key === hash);
    if (idx > -1) {
      return this.activeRequests[idx].value;
    }

    let apiResponse = this._http.put(baseUrlEnabled ? environment.baseUrl + url : url, body, requestOptions || {})
      .map(response => {
        const data = !!response ? response : {};
        if (isCacheEnabled) {
          return this.saveDataToCache(hash, data, cacheJsonInput, Model);
        } else {
          remove(this.activeRequests, request => request.key === hash);
          if (!Model) {
            return data;
          }
          return data instanceof Array ? Model.fromJSONArray(data) : Model.fromJSON(data);
        }
      })
      .catch(response => {
        remove(this.activeRequests, request => request.key === hash);
        return Observable.throw(response);
      });

    apiResponse = apiResponse.share().map(data => cloneDeep(data));
    this.activeRequests.push({ key: hash, value: apiResponse });
    return apiResponse;
  }

  private getCacheJSON(hash, cacheJsonInput?: any) {
    return this._cacheFactoryApi.get(hash, !!cacheJsonInput ? cacheJsonInput.name : null);
  }

  private saveDataToCache(hash, data, cacheJsonInput?: any, Model?: { fromJSON(json: any): EntityBase, fromJSONArray(json: any[]): EntityBase[] }) {
    let name: string;
    let options: any;
    if (!!cacheJsonInput) {
      name = cacheJsonInput.name;
      options = cacheJsonInput.options;
    }

    if (!Model) {
      this._cacheFactoryApi.put(hash, data, name, options);
      return data;
    }

    const apiResponse = data instanceof Array ? Model.fromJSONArray(data) : Model.fromJSON(data);
    this._cacheFactoryApi.put(hash, apiResponse, name, options);
    remove(this.activeRequests, request => request.key === hash);

    return apiResponse;
  }

  protected removeFromCache(key: string) {
    this._cacheFactoryApi.remove(key, 'uw-cache');
  }

  private handleError(error: Response | any) {
    let errMsg = (error.message) ? error.message : error.status ? `${error.status} - ${error.statusText}` : this.errorMsg;
    console.error(error, errMsg); // log to console instead
    return Observable.throw(errMsg);
  }

}



// WEBPACK FOOTER //
// ./src/app/core/services/base-api.service.ts